import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase.ts';
import { formatSlotDay, formatSlotRange } from '../lib/format.ts';
import type { Slot } from '../lib/types.ts';

interface SlotPickerProps {
  locationId: string;
  timezone: string;
  accentColor: string;
  value: string | null;
  onChange: (slotStartAt: string | null) => void;
}

async function fetchSlots(locationId: string): Promise<Slot[]> {
  const { data, error } = await supabase.rpc('get_available_slots', {
    p_location_id: locationId,
  });
  if (error) throw error;
  return (data ?? []) as Slot[];
}

export function SlotPicker({
  locationId,
  timezone,
  accentColor,
  value,
  onChange,
}: SlotPickerProps) {
  const { data: slots = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['slots', locationId],
    queryFn: () => fetchSlots(locationId),
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
  });

  // Auto-select first available slot
  useEffect(() => {
    if (!value && slots.length > 0) {
      const first = slots.find((s) => s.is_first_available);
      if (first) onChange(first.slot_start_at);
    }
  }, [slots, value, onChange]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2" aria-busy="true" aria-label="Загрузка времени">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="h-12 animate-pulse rounded-xl"
            style={{ background: 'var(--border)' }}
          />
        ))}
      </div>
    );
  }

  if (isError || slots.length === 0) {
    return (
      <div
        className="rounded-xl p-4 text-center"
        style={{ background: 'var(--accent-soft)', border: '1px solid var(--accent)' }}
      >
        <p className="text-sm font-medium" style={{ color: 'var(--accent)' }}>
          {isError ? 'Не удалось загрузить доступное время' : 'Нет доступного времени для заказа'}
        </p>
        {isError && (
          <button
            onClick={() => refetch()}
            className="mt-2 text-sm underline"
            style={{ color: 'var(--accent)' }}
          >
            Попробовать снова
          </button>
        )}
      </div>
    );
  }

  // Group by day
  const days = new Map<string, Slot[]>();
  for (const slot of slots) {
    const day = formatSlotDay(slot.slot_start_at, timezone);
    if (!days.has(day)) days.set(day, []);
    days.get(day)!.push(slot);
  }

  return (
    <div className="flex flex-col gap-4" role="group" aria-label="Выберите время">
      {Array.from(days.entries()).map(([day, daySlots]) => (
        <div key={day}>
          <p className="mb-2 text-sm font-semibold" style={{ color: 'var(--text)' }}>
            {day}
          </p>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {daySlots.map((slot) => {
              const isSelected = slot.slot_start_at === value;
              const almostFull = slot.remaining_units <= 2 && slot.remaining_units > 0;

              return (
                <button
                  key={slot.slot_start_at}
                  onClick={() => onChange(slot.slot_start_at)}
                  aria-pressed={isSelected}
                  aria-label={`${formatSlotRange(slot.slot_start_at, slot.slot_end_at, timezone)}${almostFull ? ', мало мест' : ''}`}
                  className="flex flex-col items-center justify-center rounded-xl py-2.5 px-1 text-sm transition-colors"
                  style={{
                    background: isSelected ? accentColor : 'var(--surface)',
                    border: `1px solid ${isSelected ? accentColor : 'var(--border)'}`,
                    color: isSelected ? '#fff' : 'var(--text)',
                    minHeight: 52,
                  }}
                >
                  <span className="font-medium tabular-nums">
                    {formatSlotRange(slot.slot_start_at, slot.slot_end_at, timezone)}
                  </span>
                  {almostFull && (
                    <span
                      className="mt-0.5 text-xs"
                      style={{ color: isSelected ? 'oklch(100% 0 0 / 0.8)' : 'var(--warning)' }}
                    >
                      мало мест
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
